import { Injectable } from '@angular/core';
import { Router, CanActivate, RouterStateSnapshot, ActivatedRouteSnapshot } from '@angular/router';
import { AuthenticationService } from './authentication.service';
import { UsersService } from './users.service';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class AuthGuardService implements CanActivate {

  constructor(
    public auth: AuthenticationService,
    public router: Router,
    private userService: UsersService) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    if (this.auth.isAuthenticated()) {
      return true;
    }
    // In-memory auth flag is false (e.g. after page refresh).
    // Try the API — if a valid httpOnly cookie exists the request will succeed.
    try {
      await firstValueFrom(this.userService.getCurrent());
      this.auth.setAuthenticated();
      return true;
    } catch {
      this.router.navigate(['signin'], { queryParams: { returnUrl: state.url } });
      return false;
    }
  }
}

@Injectable()
export class ValidSubscription implements CanActivate {

  constructor(
    public auth: AuthenticationService, 
    public router: Router,
    private userService: UsersService) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    try {
      const valid = await this.userService.subscriptionValid();
      if (!valid) {
        this.router.navigate(['account', 'subscription'], { queryParams: { returnUrl: state.url }});
        return false;
      }
      return true;
    } catch {
      // Subscription check failed (e.g. network error). Fail open to avoid
      // leaving the user stranded with an unhandled router error.
      return true;
    }
  }
}

@Injectable()
export class NoAuthGuardService implements CanActivate {

  constructor(
    public auth: AuthenticationService,
    public router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if (this.auth.isAuthenticated()) {
      this.router.navigate(['home']);
      return false;
    }
    return true;
  }
}

/**
 * Guards the root landing route: redirects authenticated users to /courses/home,
 * allows unauthenticated users through to the landing page.
 * Safe to use synchronously because AuthenticationService.initialize() runs as
 * APP_INITIALIZER before any route is evaluated.
 */
@Injectable()
export class LandingRedirectGuard implements CanActivate {

  constructor(
    public auth: AuthenticationService,
    public router: Router
  ) {}

  canActivate(): boolean {
    if (this.auth.isAuthenticated()) {
      this.router.navigate(['courses', 'home']);
      return false;
    }
    return true;
  }
}